/*
  T1 header behaviour (handover/docs/site-behaviour-spec.md > Header).
  - Compact header after 160 px of scroll.
  - Product pages: header hides while scrolling down and returns on scroll up.
  - Desktop dropdowns and the Shop panel: click, hover (mouse only), Esc, click outside.
  - Search panel and the mobile menu drawer.
  Publishes --header-offset on <html>: the visible header height, for sticky bars below it.
*/
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.section-header') || this;
    this.hideOnScroll = this.hasAttribute('data-hide-on-scroll');
    this.overlay = this.querySelector('[data-overlay]');
    this.drawer = this.querySelector('dialog.drawer');
    this.lastY = window.scrollY;
    this.ticking = false;
    this.openToggle = null;
    this.hoverTimer = null;
    this.openedAt = 0;
    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();

    this.setupDropdowns();
    this.setupSearch();
    this.setupDrawer();

    this.onKeydown = (event) => {
      if (event.key !== 'Escape' || !this.openToggle) return;
      const toggle = this.openToggle;
      this.closeAll();
      toggle.focus();
    };
    document.addEventListener('keydown', this.onKeydown);

    this.onDocClick = (event) => {
      if (this.openToggle && !this.contains(event.target)) this.closeAll();
    };
    document.addEventListener('click', this.onDocClick);
    this.overlay?.addEventListener('click', () => this.closeAll());
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocClick);
  }

  /* ---------- Scroll states ---------- */
  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    const y = window.scrollY;
    const compact = y > 160;
    this.section.classList.toggle('is-compact', compact);

    if (this.hideOnScroll) {
      const goingDown = y > this.lastY + 4;
      const goingUp = y < this.lastY - 4;
      if (goingDown && compact && !this.openToggle) this.section.classList.add('is-hidden');
      if (goingUp || !compact) this.section.classList.remove('is-hidden');
    }
    if (Math.abs(y - this.lastY) > 4) this.lastY = y;

    const hidden = this.section.classList.contains('is-hidden');
    const height = hidden ? 0 : this.querySelector('.header').offsetHeight;
    document.documentElement.style.setProperty('--header-offset', `${height}px`);
  }

  /* ---------- Dropdowns and Shop panel ---------- */
  setupDropdowns() {
    this.querySelectorAll('[data-dropdown-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        // A click straight after hover opened it should not close it again
        if (isOpen && Date.now() - this.openedAt < 400) return;
        isOpen ? this.closeAll() : this.open(toggle);
      });

      const item = toggle.closest('[data-nav-item]');
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      [item, panel].forEach((el) => {
        el?.addEventListener('mouseenter', () => {
          if (!this.canHover.matches) return;
          clearTimeout(this.hoverTimer);
          if (this.openToggle !== toggle) this.open(toggle);
        });
        el?.addEventListener('mouseleave', () => {
          if (!this.canHover.matches) return;
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => this.closeAll(), 200);
        });
      });

      // Close when keyboard focus leaves the item and its panel
      [item, panel].forEach((el) => {
        el?.addEventListener('focusout', (event) => {
          const next = event.relatedTarget;
          if (next && (item.contains(next) || panel?.contains(next))) return;
          if (next && this.openToggle === toggle) this.closeAll();
        });
      });
    });
  }

  open(toggle) {
    this.closeAll();
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.openToggle = toggle;
    this.openedAt = Date.now();
    this.section.classList.remove('is-hidden');
    if (panel.classList.contains('shop-panel') && this.overlay) this.overlay.hidden = false;
  }

  closeAll() {
    this.querySelectorAll('[aria-expanded="true"][aria-controls]').forEach((toggle) => {
      if (toggle.hasAttribute('data-drawer-open')) return;
      toggle.setAttribute('aria-expanded', 'false');
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.hidden = true;
    });
    if (this.overlay) this.overlay.hidden = true;
    this.openToggle = null;
  }

  /* ---------- Search ---------- */
  setupSearch() {
    const toggle = this.querySelector('[data-search-toggle]');
    if (!toggle) return;
    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) return this.closeAll();
      this.open(toggle);
      document.getElementById(toggle.getAttribute('aria-controls'))?.querySelector('input[type="search"]')?.focus();
    });
  }

  /* ---------- Mobile drawer ---------- */
  setupDrawer() {
    const openButton = this.querySelector('[data-drawer-open]');
    if (!this.drawer || !openButton) return;

    openButton.addEventListener('click', () => {
      this.closeAll();
      this.drawer.showModal();
      openButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('drawer-open');
    });

    this.drawer.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.drawer.close());
    this.drawer.addEventListener('click', (event) => {
      if (event.target === this.drawer) this.drawer.close();
    });
    this.drawer.addEventListener('close', () => {
      openButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('drawer-open');
      openButton.focus();
    });

    // The drawer is mobile only; close it if the screen grows to desktop
    window.matchMedia('(min-width: 1200px)').addEventListener('change', (event) => {
      if (event.matches && this.drawer.open) this.drawer.close();
    });
  }
}

if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);
